"use client";

import React, { useState } from "react";
import { Sun, Moon, ArrowRight, Activity, ShieldCheck, Zap } from "lucide-react";
import { StatusBadge, NetworkTopologyGraph } from "./technical-components";

export function DualEngineSection({ onOpenDemo }: { onOpenDemo: () => void }) {
  const [activeEngine, setActiveEngine] = useState<"engine1" | "engine2">("engine1");

  return (
    <section id="dual-engine" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <StatusBadge text="01. DUAL-ENGINE ARCHITECTURE &amp; TOPOLOGY" />
          <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
            SYNCHRONIZED DUAL-ENGINE CAMPUS.
          </h2>
          <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
            A SINGLE SCHOOL CAMPUS, TWO PERFECTLY SYNCHRONIZED OPERATING ENGINES. MORNING ACADEMIC ATHLETIC DISCIPLINE TRANSITIONS AUTOMATICALLY INTO EVENING REVENUE GENERATION.
          </p>
        </div>

        {/* Engine Switcher Tabs */}
        <div className="max-w-lg mx-auto border border-[#3A3A38]/30 bg-white p-1 grid grid-cols-2 gap-1 font-mono-tech text-xs rounded-none">
          <button
            onClick={() => setActiveEngine("engine1")}
            className={`py-2.5 px-4 font-bold uppercase transition flex items-center justify-center gap-2 rounded-none ${
              activeEngine === "engine1"
                ? "bg-[#1A3C2B] text-white"
                : "bg-transparent text-[#3A3A38] hover:bg-[#1A3C2B]/5"
            }`}
          >
            <Sun className="w-3.5 h-3.5" />
            <span>ENGINE 1: DAY PE &amp; TALENT</span>
          </button>

          <button
            onClick={() => setActiveEngine("engine2")}
            className={`py-2.5 px-4 font-bold uppercase transition flex items-center justify-center gap-2 rounded-none ${
              activeEngine === "engine2"
                ? "bg-[#1A3C2B] text-white"
                : "bg-transparent text-[#3A3A38] hover:bg-[#1A3C2B]/5"
            }`}
          >
            <Moon className="w-3.5 h-3.5" />
            <span>ENGINE 2: EVENING REVENUE</span>
          </button>
        </div>

        {/* Content Display Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center border border-[#3A3A38]/30 bg-[#F7F7F5] p-8 relative">
          {/* Four Corner Markers */}
          <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-[#1A3C2B]" />
          <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-[#1A3C2B]" />
          <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-[#1A3C2B]" />
          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-[#1A3C2B]" />

          {/* Left Side Info */}
          <div className="lg:col-span-7 space-y-6 font-mono-tech">
            <div className="inline-flex items-center gap-2 border border-[#1A3C2B]/30 bg-[#1A3C2B]/10 px-3 py-1 text-[10px] uppercase font-bold text-[#1A3C2B]">
              {activeEngine === "engine1" ? "7:30 AM - 3:30 PM // ACADEMIC HOURS" : "4:00 PM - 10:00 PM // REVENUE HOURS"}
            </div>

            <h3 className="font-space font-bold text-2xl sm:text-3xl text-[#1A3C2B] uppercase">
              {activeEngine === "engine1"
                ? "INTRA-SCHOOL ATHLETIC TESTING & BIOMETRIC TALENT DISCOVERY"
                : "COMMUNITY SPORTS VENUE RENTALS & QR PASSPORT MONETIZATION"}
            </h3>

            <p className="text-xs text-[#3A3A38] leading-relaxed">
              {activeEngine === "engine1"
                ? "During academic periods, the platform belongs 100% to students and teachers. Every PE period becomes a scientific evaluation station powered by Mirwald PHV and Eurofit testing battery."
                : "During non-academic hours, idle campus sports assets are converted into high-yield community venues with automated Razorpay payouts, slot locks, and security pass verification."}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="border border-[#3A3A38]/20 bg-white p-4 space-y-1.5 rounded-none">
                <div className="font-bold text-[#1A3C2B] text-xs uppercase flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-[#1A3C2B]" />
                  <span>{activeEngine === "engine1" ? "Eurofit 6-Station Battery" : "Dynamic Hourly Rates"}</span>
                </div>
                <p className="text-[11px] text-[#3A3A38]/80 leading-tight">
                  {activeEngine === "engine1"
                    ? "Standardized battery with automated percentile scoring & talent cohort clustering."
                    : "Peak-hour automated pricing rules generating ₹4-8 Lakhs monthly passive revenue."}
                </p>
              </div>

              <div className="border border-[#3A3A38]/20 bg-white p-4 space-y-1.5 rounded-none">
                <div className="font-bold text-[#1A3C2B] text-xs uppercase flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#1A3C2B]" />
                  <span>{activeEngine === "engine1" ? "DPDP Act Privacy Shield" : "QR Gate Pass Crypto"}</span>
                </div>
                <p className="text-[11px] text-[#3A3A38]/80 leading-tight">
                  {activeEngine === "engine1"
                    ? "Biometric consent vault & encrypted parent mobile passports."
                    : "Encrypted QR security token for non-student visitor campus gate pass."}
                </p>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={onOpenDemo}
                className="px-5 py-2.5 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition border border-[#1A3C2B] text-xs uppercase tracking-wider font-bold rounded-none flex items-center gap-2"
              >
                <span>TEST DUAL-ENGINE ARCHITECTURE</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#9EFFBF]" />
              </button>
            </div>
          </div>

          {/* Right Side Network Topology Graph */}
          <div className="lg:col-span-5 flex justify-center">
            <NetworkTopologyGraph />
          </div>
        </div>
      </div>
    </section>
  );
}
